import React from "react";
import { useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import CustomModal from "../Modals/CustomModal";
import { BASE, BORDER, BRAND, GREY } from "../../utils/constants/colors";
import { fontSize, fontWeight } from "../../utils/constants/UI";
import { PROFILE_PATH } from "../../utils/constants/routes";
import { useTour } from "../../context/TourContext";

const LEARNING_CENTER_TAB = "LearningCenter";

const TourFinishModal = () => {
  const navigate = useNavigate();
  const { finishModalOpen, finishModalData, closeFinishModal, startTour } = useTour();

  const isFullTour = finishModalData?.isFullTour ?? true;
  const nextTour = finishModalData?.nextTour ?? null;
  const firstTour = finishModalData?.firstTour ?? null;

  // Last-in-sequence: singular path, no next tour, not the full pipeline
  const isLastInSequence = !isFullTour && !nextTour;

  const handleGoToLearningCenter = () => {
    closeFinishModal();
    navigate(`${PROFILE_PATH}?tab=${LEARNING_CENTER_TAB}`);
  };

  const handleStartNextTour = () => {
    closeFinishModal();
    if (nextTour) startTour(nextTour.key, 0);
  };

  const handleCycleBack = () => {
    closeFinishModal();
    if (firstTour) startTour(firstTour.key, 0);
  };

  // Body copy per scenario
  let bodyText;
  if (isFullTour) {
    bodyText =
      "You have completed the full ReachIQ tour. Go ahead and explore the platform on your own now. To revisit any tour, head to the Learning Center anytime.";
  } else if (nextTour) {
    bodyText =
      "This tour is complete. The next one is available below if you would like to continue.";
  } else {
    bodyText =
      "You have completed the last tour in the sequence. If you would like to go through everything from the beginning, you can cycle back to the first tour below.";
  }

  // Whether to show the "Access Anytime From" info box (full tour only)
  const showAccessBox = isFullTour;
  // Whether to show the secondary CTA (next tour or cycle back)
  const hasSecondaryCta = (!isFullTour && nextTour) || (isLastInSequence && firstTour);
  const secondaryCtalabel = nextTour
    ? `Start ${nextTour.name}`
    : firstTour
    ? `Start ${firstTour.name}`
    : null;
  const handleSecondaryCta = nextTour ? handleStartNextTour : handleCycleBack;

  return (
    <CustomModal
      open={finishModalOpen}
      onClose={closeFinishModal}
      width="420px"
      variant="primary"
      header={{
        heading: {
          id: "finish-modal-heading",
          label: "Tour Complete!",
        },
      }}
    >
      <Box style={{ padding: "4px 0 8px" }}>
        <p
          style={{
            margin: 0,
            fontSize: fontSize.SM,
            fontWeight: fontWeight.REGULAR,
            color: GREY.SECONDARY,
            lineHeight: "22px",
          }}
        >
          {bodyText}
        </p>

        {/* "Up Next" card — partial tour with a following tour available */}
        {!isFullTour && nextTour && (
          <Box
            style={{
              marginTop: "16px",
              padding: "12px 16px",
              border: `1px solid ${BORDER.DEFAULT}`,
              borderRadius: "8px",
              background: BASE.BACKGROUND_LIGHT,
            }}
          >
            <p
              style={{
                margin: "0 0 4px 0",
                fontSize: "10px",
                fontWeight: fontWeight.SEMIBOLD,
                color: GREY.TERTIARY,
                textTransform: "uppercase",
                letterSpacing: "0.07em",
              }}
            >
              Up Next:
            </p>
            <span
              style={{
                fontSize: fontSize.SM,
                fontWeight: fontWeight.SEMIBOLD,
                color: GREY.PRIMARY,
                lineHeight: "20px",
              }}
            >
              {nextTour.name}
            </span>
          </Box>
        )}

        {/* "Start From Beginning" card — last tour in singular sequence */}
        {isLastInSequence && firstTour && (
          <Box
            style={{
              marginTop: "16px",
              padding: "12px 16px",
              border: `1px solid ${BORDER.DEFAULT}`,
              borderRadius: "8px",
              background: BASE.BACKGROUND_LIGHT,
            }}
          >
            <p
              style={{
                margin: "0 0 4px 0",
                fontSize: "10px",
                fontWeight: fontWeight.SEMIBOLD,
                color: GREY.TERTIARY,
                textTransform: "uppercase",
                letterSpacing: "0.07em",
              }}
            >
              Start From Beginning:
            </p>
            <span
              style={{
                fontSize: fontSize.SM,
                fontWeight: fontWeight.SEMIBOLD,
                color: GREY.PRIMARY,
                lineHeight: "20px",
              }}
            >
              {firstTour.name}
            </span>
          </Box>
        )}

        {/* "Access Anytime From" box — full tour completion only */}
        {showAccessBox && (
          <Box
            style={{
              marginTop: "16px",
              padding: "12px 16px",
              border: `1px solid ${BORDER.DEFAULT}`,
              borderRadius: "8px",
              background: BASE.BACKGROUND_LIGHT,
            }}
          >
            <p
              style={{
                margin: "0 0 10px 0",
                fontSize: "10px",
                fontWeight: fontWeight.SEMIBOLD,
                color: GREY.TERTIARY,
                textTransform: "uppercase",
                letterSpacing: "0.07em",
              }}
            >
              Access Anytime From:
            </p>
            <Box style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <span
                style={{
                  fontSize: fontSize.SM,
                  fontWeight: fontWeight.MEDIUM,
                  color: GREY.PRIMARY,
                  lineHeight: "20px",
                }}
              >
                Settings{" "}
                <span style={{ color: GREY.TERTIARY, fontWeight: fontWeight.REGULAR }}>
                  {"→"}
                </span>{" "}
                <strong>Learning Center</strong>
              </span>
            </Box>
          </Box>
        )}

        {/* Secondary CTA: "Start next tour" or "Cycle back to first tour" */}
        {hasSecondaryCta && secondaryCtalabel && (
          <button
            onClick={handleSecondaryCta}
            style={{
              display: "block",
              width: "100%",
              marginTop: "20px",
              padding: "10px 20px",
              background: BASE.WHITE,
              color: BRAND.PRIMARY,
              border: `1.5px solid ${BRAND.PRIMARY}`,
              borderRadius: "7px",
              fontSize: fontSize.SM,
              fontWeight: fontWeight.SEMIBOLD,
              cursor: "pointer",
              lineHeight: "20px",
              letterSpacing: "0.01em",
              fontFamily: "inherit",
            }}
          >
            {secondaryCtalabel}
          </button>
        )}

        {/* "Go to Learning Center" — always shown */}
        <button
          onClick={handleGoToLearningCenter}
          style={{
            display: "block",
            width: "100%",
            marginTop: hasSecondaryCta ? "8px" : "20px",
            padding: "10px 20px",
            background: BRAND.PRIMARY,
            color: BASE.WHITE,
            border: "none",
            borderRadius: "7px",
            fontSize: fontSize.SM,
            fontWeight: fontWeight.SEMIBOLD,
            cursor: "pointer",
            lineHeight: "20px",
            letterSpacing: "0.01em",
            fontFamily: "inherit",
          }}
        >
          Go to Learning Center
        </button>
      </Box>
    </CustomModal>
  );
};

export default TourFinishModal;
